import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/for-bartenders")({
  head: () => ({
    meta: [
      { title: "For bartenders — Pick up bar shifts near you | The Hospitality Reserve" },
      { name: "description", content: "Get paid to pour. Pick up vetted bar shifts near you, keep 100% of your rate, and get paid within 3–5 business days of every shift." },
      { property: "og:title", content: "For bartenders — The Hospitality Reserve" },
      { property: "og:description", content: "Vetted venues. Fair pay. Payouts in 3–5 business days." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thehospitalityreserve.com/for-bartenders" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/for-bartenders" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "Do I keep 100% of my hourly rate?", acceptedAnswer: { "@type": "Answer", text: "Yes. The venue pays the platform fee separately — you get your full rate for every hour worked." } },
          { "@type": "Question", name: "How fast do I get paid?", acceptedAnswer: { "@type": "Answer", text: "Payouts reach your bank account within 3–5 business days of a completed shift, as Stripe needs time to process the transfer." } },
          { "@type": "Question", name: "What checks are required?", acceptedAnswer: { "@type": "Answer", text: "An automated CV experience check (minimum 6 months combined bar experience), a Right to Work check via Veriff, and confirmation you hold Public Liability Insurance." } },
        ],
      }),
    }],
  }),
  component: ForBartendersPage,
});

function ForBartendersPage() {
  return (
    <main className="mx-auto max-w-4xl space-y-16 p-6 py-16">
      <header className="space-y-4">
        <p className="text-[0.6rem] tracking-wide-luxury uppercase text-accent">For bartenders</p>
        <h1 className="text-4xl font-medium md:text-5xl">Pick up bar shifts near you. Keep 100% of your rate.</h1>
        <p className="text-lg text-muted-foreground">
          Vetted venues. Fair pay. Payouts in 3–5 business days. Build a reputation, unlock better shifts, and get paid to do what you love.
        </p>
        <div className="flex gap-3">
          <Link to="/auth" search={{ mode: "signup", role: "bartender" } as never} className="border border-accent bg-accent px-5 py-3 text-sm uppercase tracking-wide-luxury text-accent-foreground">Join The Reserve</Link>
          <Link to="/install" className="border border-border px-5 py-3 text-sm uppercase tracking-wide-luxury">Get the app</Link>
        </div>
      </header>

      <section className="grid gap-6 md:grid-cols-3">
        {[
          { h: "Full hourly rate", p: "Venues pay the platform fee separately. You keep every penny of your agreed rate." },
          { h: "Fast payouts", p: "Get paid within 3–5 business days of a completed shift, once your payout details are set up." },
          { h: "Referral rewards", p: "£10 credit when a friend you refer completes their first shift." },
        ].map((c) => (
          <div key={c.h} className="border border-border p-6">
            <h2 className="text-sm uppercase tracking-wide-luxury text-accent">{c.h}</h2>
            <p className="mt-3 text-sm text-muted-foreground">{c.p}</p>
          </div>
        ))}
      </section>

      <section className="border border-border p-8 space-y-6">
        <h2 className="text-2xl font-medium">How it works</h2>
        <ol className="space-y-4 text-sm">
          <li><strong>1. Get vetted.</strong> An automated CV experience check, Veriff RTW check, and PLI confirmation.</li>
          <li><strong>2. Browse the register.</strong> Filter by distance, rate, and role. Save shifts for later or apply instantly.</li>
          <li><strong>3. Work &amp; get paid.</strong> Confirm arrival, complete the shift, and get paid — typically within 3–5 business days.</li>
        </ol>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-medium">Common questions</h2>
        {[
          ["Do I keep 100% of my hourly rate?", "Yes. The venue pays the platform fee separately — you get your full rate for every hour worked."],
          ["How fast do I get paid?", "Payouts reach your bank account within 3–5 business days of a completed shift, as Stripe needs time to process the transfer."],
          ["What checks are required?", "An automated CV experience check (minimum 6 months combined bar experience), a Right to Work check via Veriff, and confirmation you hold Public Liability Insurance."],
        ].map(([q, a]) => (
          <details key={q} className="border border-border p-5">
            <summary className="cursor-pointer text-sm font-medium">{q}</summary>
            <p className="mt-3 text-sm text-muted-foreground">{a}</p>
          </details>
        ))}
      </section>
    </main>
  );
}
